'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import styled, { useTheme } from 'styled-components';
import { easeInOutCubic, timingFaster } from '../../constants';
import { TypeStyle } from '../TypeStyle';
function getBadgeHoverColors(theme, colorVariant) {
    switch (colorVariant) {
        case 'primary':
            return {
                backgroundColor: theme.color.button.primary.hover.fill.on.background,
                borderColor: theme.color.button.primary.hover.border.on.background,
                color: theme.color.button.primary.hover.content.on.background,
            };
        case 'secondary':
            return {
                backgroundColor: theme.color.button.secondary.hover.fill.on.background,
                borderColor: theme.color.button.secondary.hover.border.on.background,
                color: theme.color.button.secondary.hover.content.on.background,
            };
        case 'ghost':
            return {
                backgroundColor: theme.color.button.secondary.hover.fill.on.background,
                borderColor: theme.color.stroke.neutral.border.decoration.on.background,
                color: theme.color.button.secondary.hover.content.on.background,
            };
        case 'neutral':
            return {
                backgroundColor: theme.color.button.disabled.fill.on.background,
                borderColor: theme.color.button.tertiary.hover.border.on.background,
                color: theme.color.button.tertiary.hover.content.on.background,
            };
        case 'critical':
            return {
                backgroundColor: theme.color.button.critical.hover.fill.on.background,
                borderColor: theme.color.button.critical.hover.border.on.background,
                color: theme.color.button.critical.hover.content.on.background,
            };
        case 'celebratory':
            return {
                backgroundColor: theme.color.button.celebratory.hover.fill.on.background,
                borderColor: theme.color.button.celebratory.hover.border.on.background,
                color: theme.color.button.celebratory.hover.content.on.background,
            };
        case 'caution':
            return {
                backgroundColor: theme.color.button.caution.hover.fill.on.background,
                borderColor: theme.color.button.caution.hover.border.on.background,
                color: theme.color.button.caution.hover.content.on.background,
            };
        case 'subtle':
            return {
                backgroundColor: theme.color.button.disabled.fill.on.background,
                borderColor: theme.color.button.tertiary.hover.border.on.background,
                color: theme.color.button.tertiary.hover.content.on.background,
            };
        case 'info':
            return {
                backgroundColor: theme.color.button.info.hover.fill.on.background,
                borderColor: theme.color.button.info.hover.border.on.background,
                color: theme.color.button.info.hover.content.on.background,
            };
        case 'default':
            return {
                backgroundColor: theme.color.button.disabled.fill.on.background,
                borderColor: theme.color.button.tertiary.hover.border.on.background,
                color: theme.color.button.tertiary.hover.content.on.background,
            };
        default:
            return getBadgeColors(theme, colorVariant);
    }
}
function getBadgeColors(theme, colorVariant) {
    switch (colorVariant) {
        case 'primary':
            return {
                backgroundColor: theme.color.button.primary.fill.on.background,
                borderColor: theme.color.button.primary.border.on.background,
                color: theme.color.button.primary.content.on.background,
            };
        case 'secondary':
            return {
                backgroundColor: theme.color.button.secondary.fill.on.background,
                borderColor: theme.color.button.secondary.border.on.background,
                color: theme.color.button.secondary.content.on.background,
            };
        case 'ghost':
            return {
                backgroundColor: theme.color.button.secondary.fill.on.background,
                borderColor: theme.color.stroke.neutral.border.decoration.on.background,
                color: theme.color.button.secondary.content.on.background,
            };
        case 'neutral':
            return {
                backgroundColor: theme.color.button.tertiary.fill.on.background,
                borderColor: theme.color.button.tertiary.border.on.background,
                color: theme.color.button.tertiary.content.on.background,
            };
        case 'critical':
            return {
                backgroundColor: theme.color.fill.critical.subtle,
                borderColor: theme.color.fill.critical.subtle,
                color: theme.color.stroke.critical.text.on.subtle,
            };
        case 'celebratory':
            return {
                backgroundColor: theme.color.fill.celebratory.subtle,
                borderColor: theme.color.fill.celebratory.subtle,
                color: theme.color.stroke.celebratory.text.highContrast.on.subtle,
            };
        case 'caution':
            return {
                backgroundColor: theme.color.fill.caution.subtle,
                borderColor: theme.color.fill.caution.subtle,
                color: theme.color.stroke.caution.text.on.subtle,
            };
        case 'subtle':
            return {
                backgroundColor: theme.color.fill.subtle,
                borderColor: theme.color.fill.subtle,
                color: theme.color.text.primary.on.background,
            };
        case 'highlight':
            return {
                backgroundColor: theme.color.badge.highlight.fill.on.background,
                borderColor: theme.color.badge.highlight.border.on.background,
                color: theme.color.badge.highlight.content.on.background,
            };
        case 'info':
            return {
                backgroundColor: theme.color.fill.info.subtle,
                borderColor: theme.color.fill.info.subtle,
                color: theme.color.stroke.info.text.on.subtle,
            };
        case 'disabled':
            return {
                backgroundColor: theme.color.fill.disabled.subtle,
                borderColor: theme.color.stroke.neutral.border.decoration.on.subtle,
                color: theme.color.button.tertiary.content.on.background,
            };
        case 'default':
        default:
            return {
                backgroundColor: theme.color.button.tertiary.fill.on.background,
                borderColor: theme.color.button.tertiary.border.on.background,
                color: theme.color.button.tertiary.content.on.background,
            };
    }
}
const sizeConfig = {
    small: {
        padding: {
            default: '4px 10px',
            label: '7px 12px',
        },
    },
    medium: {
        padding: {
            default: '6px 12px',
            label: '8px 16px',
        },
    },
    large: {
        padding: {
            default: '8px 16px',
            label: '10px 20px',
        },
    },
};
const getTypeStyleForSize = (size) => {
    switch (size) {
        case 'small':
            return TypeStyle.css.bodyTiny;
        case 'medium':
            return TypeStyle.css.bodyNano;
        case 'large':
            return TypeStyle.css.bodyMicro;
        default:
            return TypeStyle.css.bodyNano;
    }
};
const StyledBadge = styled.div `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${o => o.theme.spacing(2)};
  box-sizing: border-box;
  text-align: center;
  transition:
    ${timingFaster} ${easeInOutCubic} background-color,
    ${timingFaster} ${easeInOutCubic} color,
    ${timingFaster} ${easeInOutCubic} border-color,
    ${timingFaster} ${easeInOutCubic} opacity;

  padding: ${({ $variant, $size }) => {
    const config = sizeConfig[$size];
    return $variant === 'label' ? config.padding.label : config.padding.default;
}};
  border-radius: ${({ $variant }) => ($variant === 'pill' ? '20px' : '4px')};
  border-style: solid;
  border-width: 1px;
  border-color: ${({ $borderless, $variantColors }) => $borderless ? 'transparent' : $variantColors.borderColor};
  background-color: ${({ $variantColors }) => $variantColors.backgroundColor};
  color: ${({ $variantColors }) => $variantColors.color};
  text-transform: ${o => o.$textTransform};
  font-weight: ${o => o.$fontWeight};

  ${o => getTypeStyleForSize(o.$size)};
  ${o => o.$textTransform === 'uppercase' && o.theme.typography.effect.uppercase};

  ${({ $withHover, $hoverVariantColors, $borderless }) => $withHover &&
    `
      &:hover {
        background-color: ${$hoverVariantColors.backgroundColor};
        color: ${$hoverVariantColors.color};
        border-color: ${$borderless ? 'transparent' : $hoverVariantColors.borderColor};
      }
    `}
`;
export const Badge = function Badge({ variant = 'badge', size = 'medium', colorVariant = 'default', textTransform = 'uppercase', fontWeight = '600', borderless, withHover = false, children, as, ref, ...rest }) {
    const theme = useTheme();
    const variantColors = getBadgeColors(theme, colorVariant);
    const hoverVariantColors = getBadgeHoverColors(theme, colorVariant);
    return (_jsx(StyledBadge, { ref: ref, as: as, "$variant": variant, "$size": size, "$colorVariant": colorVariant, "$variantColors": variantColors, "$hoverVariantColors": hoverVariantColors, "$textTransform": textTransform, "$fontWeight": fontWeight, "$borderless": borderless, "$withHover": withHover, ...rest, children: children }));
};
//# sourceMappingURL=Badge.js.map